/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type ColumnDef, I18nService, UNPLACEHOLDER, UtilService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { MO } from 'app/common/constants/mo';
import { ToolService } from 'app/common/services';
import { FeatureConfig } from 'app/config/feature';

import { LifProtoCol } from './../../../../common/constants/index';
import { PortGroupMemberTypeE } from './port-group.const';

@Injectable()
export class PortGroupStrService {
    lifProtocolOpt = {
        0: this.i18n.get('common.term_confirm_none'),
        1: this.i18n.get('NFS'),
        2: this.i18n.get('CIFS'),
        3: this.i18n.get('NFS + CIFS'),
        4: this.i18n.get('iSCSI'),
        8: this.i18n.get('FC/FCoE'),
        64: this.i18n.get('common.NVMeoF_RoCE_lab'),
    };

    lifProtocolOptions: SelectOption[] = [
        ... (FeatureConfig.IsHideIscsiLif ? [] :
            [
                {
                    label: this.lifProtocolOpt[4],
                    value: LifProtoCol.ISCSI,
                },
            ]),
        ... (FeatureConfig.IsSupportNVMEOverRoCENetWorkAndLif ? [
            {
                label: this.lifProtocolOpt[64],
                value: LifProtoCol.NVMe_Over_RoCE,
            },
        ] : []),
    ];

    constructor(
        private i18n: I18nService,
        private utilService: UtilService,
    ) { }

    /**
     * i18n冒号
     */
    colon = this.i18n.get('common.colon_label');
    brackets = this.i18n.get('common.brackets_connection_lab');
    i18nLabel = {
        vstoreLab: this.i18n.get('common.owningVstore_lab'),
        createBtn: this.i18n.get('common.create_button'),
        deleteBtn: this.i18n.get('common.delete_button'),
        closeBtn: this.i18n.get('common.close_button'),
        addBtn: this.i18n.get('common.add_button'),
        okBtn: this.i18n.get('common.confirm_button'),
        cancelBtn: this.i18n.get('common.cancel_button'),
        removeBtn: this.i18n.get('common.remove_button'),
        modifyBtn: this.i18n.get('common.modify_button'),
        cancelModifyBtn: this.i18n.get('common.cancelModify_button'),
        unmapToDeleteTip: this.i18n.get('common.LUNGroup_task_delete_error_tips'),

        portTypeLabel: this.i18n.get('common.portType_label'),
        portNum: this.i18n.get('host.portGroup_term_portsNum_cloum'),
        actionBtn: this.i18n.get('common.operation_button'),
        summary: this.i18n.get('common.task_collectInfo_title_label'),
        topology: this.i18n.get('common.topology_lab'),
        memberPorts: this.i18n.get('host.portGroup_view_memberPort_title'),
        description: this.i18n.get('common.description_column'),

        status: this.i18n.get('common.status_column'),
        runningStatus: this.i18n.get('common.runningStatus_column'),
        healthStatus: this.i18n.get('common.healthStatus_column'),
        ID: this.i18n.get('common.ID_column'),
        location: this.i18n.get('common.location_column'),
        WWPN: this.i18n.get('common.WWPN_lab'),
        type: this.i18n.get('common.type_column'),
        unknown: this.i18n.get('common.EnumDefine_RUNNING_STATUS_E_UNKNOWN'),
        online: this.i18n.get('common.EnumDefine_RUNNING_STATUS_E_ONLINE'),
        offline: this.i18n.get('common.EnumDefine_RUNNING_STATUS_E_OFFLINE'),
        deleting: this.i18n.get('common.EnumDefine_RUNNING_STATUS_REMOVING'),
        modifying: this.i18n.get('common.modifying_lab'),


        name: this.i18n.get('common.name_column'),
        createPortGroup: this.i18n.get('host.portGroup_task_create_title'),

        new: this.i18n.get('common.newCreate_checkbox'),
        existed: this.i18n.get('common.Existed_checkbox'),
        portGroup: this.i18n.get('common.portGroup_title'),
        portGroups: this.i18n.get('common.portGroups_title'),

        mapping: this.i18n.get('common.mapping_column'),
        mapped: this.i18n.get('common.mapping_mapped_value'),
        unmapped: this.i18n.get('common.mapping_unMapped_value'),

        addPort: this.i18n.get('host.portGroup_task_addPort_title'),
        addPortButton: this.i18n.get('host.portGroup_term_addPort_button'),
        unConnectedPortTip: this.i18n.get('host.portGroup_task_create_showNoConnectPort_desc'),

        removePort: this.i18n.get('host.portGroup_task_removePort_confirm_title'),
        removePortButton: this.i18n.get('host.portGroup_term_removePort_button'),
        removePortTip: this.i18n.get('host.hostGroup_task_remove_host_selecthost_lab'),

        editName: this.i18n.get('host.hostGroup_task_editName_title'),
        modifyPortGroup: this.i18n.get('host.portGroup_task_modify_confirm_title'),

        mappingStaus: this.i18n.get('common.mapping_column'),
        addPortToGroup: this.i18n.get('host.portGroup_task_addPort_title'),
        deletePortGroup: this.i18n.get('host.portGroup_task_delete_confirm_title'),
        selectOneObjectErr: this.i18n.get('common.selectOneObject_lab'),

        availablePorts: this.i18n.get('common.available', [this.i18n.get('common.Ports')]),
        selectedPorts: this.i18n.get('common.selected', [this.i18n.get('common.Ports')]),

        dataProtocol: this.i18n.get('system.logicPort_task_create_dataProtocol_label'),
        portGroupName: this.i18n.get('host.mappingView_task_view_portGroupName_lab'),
        physicalPortLabel: this.i18n.get('common.EnumDefine_MOTYPE_PHYSICAL_PORT'),
        lifPortLabel: this.i18n.get('common.EnumDefine_MOTYPE_LOGICAL_PORT'),
        activeStatusLabel: this.i18n.get('protection.vstore_hyperMetroDomain_task_view_activeStatus_lab'),
        removeNoPortTips: this.i18n.get('common.hostGroup_task_removePort_error_tips'),
    };

    lifActiveOpt = {
        true: this.i18n.get('common.EnumDefine_RUNNING_STATUS_E_ACTIVATED'),
        false: this.i18n.get('common.EnumDefine_RUNNING_STATUS_E_UNACTIVATED'),
    };

    portTypeOpt = {
        0: this.i18n.get('common.EnumDefine_MOTYPE_PHYSICAL_PORT'),
        1: this.i18n.get('common.EnumDefine_MOTYPE_LOGICAL_PORT'),
        16: UNPLACEHOLDER,
    };

    lifActiveOptions = this.obj2Options(this.lifActiveOpt);

    promptTip = this.getI18n({
        removePort: 'host.portGroup_task_removePort_confirm_msg',
        deletePortGroup: 'host.portGroup_task_delete_confirm_msg',
    });

    restTip = {
        createPortGroup: (portGroupName): string => this.i18n.get('rest.create_portGroup_msg', [portGroupName]),
        modifyPortGroup: (portGroupName): string => this.i18n.get('rest.modify_portGroup_msg', [portGroupName]),
        deletePortGroup: (portGroupName): string => this.i18n.get('rest.delete_portGroup_msg', [portGroupName]),
        addPortToPortGroup: (port, portName): string => {
            let params = '';
            switch (port.TYPE) {
                case MO.FC_PORT_TYPE:
                    params = 'rest.addFCPort_msg';
                    break;
                case MO.ETH_PORT_TYPE:
                    params = port.physicalType === '1' ? 'rest.addEthPort_msg' : 'rest.addRoCEPort_msg';
                    break;
                case MO.LIF_PORT_TYPE:
                    params = 'rest.addLogicalPort_msg';
                    break;
                default:
                    break;
            }
            return this.i18n.get(params, [portName]);
        },
        removePortFromPortGroup: (port, portName): string => {
            let params = '';
            switch (port.TYPE) {
                case MO.FC_PORT_TYPE:
                    params = 'rest.removeFCPort_msg';
                    break;
                case MO.ETH_PORT_TYPE:
                    params = port.physicalType === '1' ? 'rest.removeEthPort_msg' : 'rest.removeRoCEPort_msg';
                    break;
                case MO.LIF_PORT_TYPE:
                    params = 'rest.removeLogicalPort_msg';
                    break;
                default:
                    break;
            }
            return this.i18n.get(params, [portName]);
        },
    };

    portObj = this.getPortObj();
    getPortObj(): { [key: string]: string } {
        const params = {
            212: 'common.EnumDefine_MOTYPE_FC_PORT1',
            '213_1': 'common.EnumDefine_MOTYPE_ETH_PORT1',
            '213_2': 'common.EnumDefine_MOTYPE_RoCE_PORT',
        };

        if (!FeatureConfig.IsSupportFC) {
            delete params['212'];
        }

        if (!FeatureConfig.IsSupportISCSI) {
            delete params['213_1'];
        }

        if (!FeatureConfig.IsSupportNVMEOverRoCENetWorkAndLif) {
            delete params['213_2'];
        }

        return this.portObj = this.getI18n(params);
    }

    portObjLif = this.getI18n({
        279: 'common.EnumDefine_MOTYPE_LOGICAL_PORT',
    });

    portRunningStatusObj = this.getI18n({
        0: 'common.EnumDefine_HEALTH_STATUS_E_UNKNOWN',
        1: 'common.EnumDefine_STATUS_E_NORMAL',
        2: 'common.EnumDefine_RUNNING_STATUS_E_RUNNING',
        10: 'common.EnumDefine_RUNNING_STATUS_E_LINK_UP',
        11: 'common.EnumDefine_RUNNING_STATUS_E_LINK_DOWN',
        33: 'common.EnumDefine_RUNNING_STATUS_E_TO_BE_RECOVERD',
    });

    /**
     * 逻辑端口-运行状态
     */
    lifPortRunningStatusOpt = this.getI18n({
        0: 'common.EnumDefine_RUNNING_STATUS_E_UNKNOWN',
        10: 'common.EnumDefine_RUNNING_STATUS_E_LINK_UP',
        11: 'common.EnumDefine_RUNNING_STATUS_E_LINK_DOWN',
        33: 'common.EnumDefine_RUNNING_STATUS_E_TO_BE_RECOVERD',
        110: 'common.EnumDefine_RUNNING_STATUS_E_TO_BE_WORK',
    });

    portHealthStatusObj = this.getI18n({
        0: 'common.EnumDefine_HEALTH_STATUS_E_UNKNOWN',
        1: 'common.EnumDefine_STATUS_E_NORMAL',
        2: 'common.EnumDefine_HEALTH_STATUS_fault',
        3: 'common.EnumDefine_HEALTH_STATUS_E_PRE_FAIL',
        7: 'common.EnumDefine_HEALTH_STATUS_E_HAS_ERR_CODE',
        9: 'common.EnumDefine_HEALTH_STATUS_E_INCONSISTENT ',
    });

    /**
     * 逻辑端口-运行状态
     */
    portTypeObj = this.getI18n({
        0: this.i18n.get('common.EnumDefine_MOTYPE_PHYSICAL_PORT'),
        1: this.i18n.get('common.EnumDefine_MOTYPE_LOGICAL_PORT'),
    });

    physicalPortTableColumnDefs: ColumnDef[] = [
        {
            field: 'newType',
            header: this.i18nLabel.type,
            filterType: 'dropdown',
            options: this.portObj,
            render: (value, col, data): number | string => col.options[data.newType],
            columnFilter: true,
            sortable: true,
        },
        {
            field: 'LOCATION',
            header: this.i18nLabel.location,
        },
    ];

    lifPortTableColumnDefs: ColumnDef[] = [
        {
            field: 'NAME',
            header: this.i18nLabel.name,
            filterType: 'search',
        },
        {
            field: 'SUPPORTPROTOCOL',
            header: this.i18nLabel.dataProtocol,
            columnFilter: true,
            filterType: 'dropdown',
            options: this.lifProtocolOptions,
            render: (value): string => this.utilService.getLabelFromOptions(this.lifProtocolOptions, Number(value)),
        },
    ];

    // 映射状态
    mappingOptions: SelectOption[] = [
        {
            label: this.i18nLabel.mapped,
            value: 'true',
        },
        {
            label: this.i18nLabel.unmapped,
            value: 'false',
        },
    ];

    mappingOpts = {
        // 数组
        true: this.i18nLabel.mapped,
        false: this.i18nLabel.unmapped,
    };

    portTypeOpts = this.obj2Options(this.portTypeObj);

    portOptions = this.obj2Options(this.portObj);

    nullErrMsg(name: string): string {
        const i18nName = this.i18nLabel[name];
        return this.i18n.get('common.null_validation', [i18nName]);
    }

    nameErrMsg(name: string): string {
        const i18nName = this.i18nLabel[name];
        return this.i18n.get('common.nameformat_validation', [i18nName]);
    }

    lengthErrMsg(min: number, max: number): string {
        return this.i18n.get('common.length_msg', [min, max]);
    }

    usageStr = (...args): string => this.i18n.get('common.usedandTotal_tip', args);

    obj2Options(obj): SelectOption[] {
        return Object.keys(obj).map((key) => {
            const option = {
                value: key,
                label: obj[key],
            };
            return option;
        });
    }

    getI18n(kv: { [key: string]: string }): { [key: string]: string } {
        const keys = Object.keys(kv);
        const result = {};
        keys.forEach((key) => {
            result[key] = this.i18n.get(kv[key]);
        });
        return result;
    }

    getPortTableColumnDefs(portType: PortGroupMemberTypeE): ColumnDef[] {
        return portType === PortGroupMemberTypeE.Lif ? this.lifPortTableColumnDefs : this.physicalPortTableColumnDefs;
    }
}
